/**
 * Parametri di regolazione (specifica tecnica §4.3) e catalogo dei comportamenti
 * dell'attuatore sul pin 12 (soluzione end-to-end §5.4).
 *
 * ⚠️ I valori predefiniti sono ASSUNTI (soluzione end-to-end §5): la logica reale
 * vive nella centralina e va validata contro l'algoritmo Arduino del committente.
 */

export interface ParametriRegolazione {
  curvaClimatica: {
    pendenza: number;
    temperaturaBase: number;
    temperaturaEsternaProgetto: number;
    mandataMin: number;
    mandataMax: number;
  };
  isteresi: {
    zona: number;
    generatore: number;
    tempoMinimoOnMin: number;
    tempoMinimoOffMin: number;
  };
  deltaT: {
    nominale: number;
    sogliaAllarme: number;
    minutiPrimaDellAllarme: number;
  };
  circolatore: {
    modalita: 'FISSO' | 'MODULANTE';
    portataMinimaPct: number;
  };
  sanitario: {
    setpoint: number;
    isteresi: number;
    prioritaSuRiscaldamento: boolean;
    tettoPrioritaMin: number;
    antilegionella: { temperatura: number; giorno: number; ora: number };
  };
  cascata: {
    ritardoSecondarioMin: number;
  };
  comportamentoPin12: CodiceComportamento;
  protezioni: {
    antigeloZona: number;
    antigeloEsterna: number;
    sovratemperaturaDelta: number;
    antibloccaggioGiorni: number;
    margineRugiada: number;
  };
  commutazioneStagionale: {
    sogliaRiscaldamento: number;
    sogliaRaffrescamento: number;
    ritardoOre: number;
  };
  vmc: {
    sogliaUmidita: number;
  };
}

export type CodiceComportamento = 1 | 2 | 3 | 4 | 5 | 6;

export interface VoceCatalogoPin12 {
  codice: CodiceComportamento;
  /** Chiave i18n della descrizione estesa: mai un numero nudo (RF-34). */
  chiave: string;
}

export const CATALOGO_COMPORTAMENTI_PIN12: readonly VoceCatalogoPin12[] = [
  { codice: 1, chiave: 'pin12.1' },
  { codice: 2, chiave: 'pin12.2' },
  { codice: 3, chiave: 'pin12.3' },
  { codice: 4, chiave: 'pin12.4' },
  { codice: 5, chiave: 'pin12.5' },
  { codice: 6, chiave: 'pin12.6' },
];

export const PARAMETRI_PREDEFINITI: ParametriRegolazione = {
  curvaClimatica: {
    pendenza: 0.6,
    temperaturaBase: 20,
    temperaturaEsternaProgetto: -5,
    mandataMin: 25,
    mandataMax: 45,
  },
  isteresi: { zona: 0.4, generatore: 2, tempoMinimoOnMin: 5, tempoMinimoOffMin: 3 },
  deltaT: { nominale: 7, sogliaAllarme: 2, minutiPrimaDellAllarme: 15 },
  circolatore: { modalita: 'MODULANTE', portataMinimaPct: 30 },
  sanitario: {
    setpoint: 48,
    isteresi: 5,
    prioritaSuRiscaldamento: true,
    tettoPrioritaMin: 30,
    antilegionella: { temperatura: 60, giorno: 0, ora: 3 },
  },
  cascata: { ritardoSecondarioMin: 20 },
  comportamentoPin12: 2,
  protezioni: {
    antigeloZona: 5,
    antigeloEsterna: 3,
    sovratemperaturaDelta: 10,
    antibloccaggioGiorni: 7,
    margineRugiada: 2,
  },
  commutazioneStagionale: { sogliaRiscaldamento: 15, sogliaRaffrescamento: 24, ritardoOre: 48 },
  vmc: { sogliaUmidita: 60 },
};

/** Temperatura di mandata dalla curva climatica (soluzione end-to-end §5.3). */
export function mandataDaCurva(
  p: ParametriRegolazione['curvaClimatica'],
  temperaturaDesiderata: number,
  temperaturaEsterna: number,
): number {
  const t = p.temperaturaBase + p.pendenza * (temperaturaDesiderata - temperaturaEsterna);
  return Math.min(p.mandataMax, Math.max(p.mandataMin, t));
}

/** Punto di rugiada approssimato (Magnus) — usato dalla protezione in raffrescamento. */
export function puntoDiRugiada(temperatura: number, umiditaPct: number): number {
  const a = 17.27;
  const b = 237.7;
  const alpha = (a * temperatura) / (b + temperatura) + Math.log(Math.max(1, umiditaPct) / 100);
  return (b * alpha) / (a - alpha);
}
